import React from "react";
import styled, { keyframes } from "styled-components";
import { useTranslation } from "react-i18next";

const flow = keyframes`
  to {
    stroke-dashoffset: -180;
  }
`;

const softPulse = keyframes`
  0%, 100% {
    opacity: 0.42;
    transform: scale(1);
  }

  50% {
    opacity: 0.92;
    transform: scale(1.05);
  }
`;

const coreSpin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const voiceMove = keyframes`
  0%, 100% {
    transform: scaleY(0.42);
  }

  45% {
    transform: scaleY(1);
  }

  72% {
    transform: scaleY(0.62);
  }
`;

const cardWake = keyframes`
  0%, 18%, 100% {
    opacity: 0.72;
  }

  28%, 44% {
    opacity: 1;
  }
`;

const outputFill = keyframes`
  0%, 35%, 100% {
    opacity: 0.42;
    transform: scaleX(0.72);
  }

  50%, 70% {
    opacity: 0.9;
    transform: scaleX(1);
  }
`;

const routeTrace = keyframes`
  to {
    stroke-dashoffset: -96;
  }
`;

const nodeSignal = keyframes`
  0%, 100% {
    opacity: 0.45;
    transform: scale(0.92);
  }

  50% {
    opacity: 1;
    transform: scale(1.08);
  }
`;

const INPUTS = [
  { id: "mail", label: { nl: "Mail", en: "Mail" }, x: 54, y: 80, icon: "mail", delay: "0s" },
  { id: "chat", label: { nl: "Chat", en: "Chat" }, x: 54, y: 202, icon: "chat", delay: "0.2s" },
  { id: "voice", label: { nl: "Spraak", en: "Voice" }, x: 54, y: 324, icon: "voice", delay: "0.4s" },
  { id: "docs", label: { nl: "Docs", en: "Docs" }, x: 54, y: 446, icon: "document", delay: "0.6s" },
  { id: "agenda", label: { nl: "Agenda", en: "Calendar" }, x: 54, y: 568, icon: "calendar", delay: "0.8s" },
];

const OUTPUTS = [
  { id: "crm", label: { nl: "CRM", en: "CRM" }, x: 684, y: 96, icon: "profile", delay: "0.1s" },
  { id: "tasks", label: { nl: "Taken", en: "Tasks" }, x: 684, y: 250, icon: "checklist", delay: "0.3s" },
  { id: "proposal", label: { nl: "Voorstel", en: "Proposal" }, x: 684, y: 404, icon: "document", delay: "0.5s" },
  { id: "dashboard", label: { nl: "Grip", en: "Control" }, x: 684, y: 558, icon: "chart", delay: "0.7s" },
];

const INPUT_PATHS = [
  "M 326 125 C 388 125 350 286 438 332",
  "M 326 247 C 372 247 352 326 438 350",
  "M 326 369 C 372 369 352 369 438 369",
  "M 326 491 C 372 491 352 414 438 388",
  "M 326 613 C 388 613 350 454 438 408",
];

const OUTPUT_PATHS = [
  "M 562 332 C 636 300 618 145 684 145",
  "M 562 350 C 632 330 626 299 684 299",
  "M 562 388 C 632 410 626 453 684 453",
  "M 562 408 C 636 454 618 607 684 607",
];

function Icon({ type, x, y }) {
  if (type === "mail") {
    return (
      <g transform={`translate(${x} ${y})`}>
        <rect className="icon-box" width="54" height="54" rx="13" />
        <path className="icon-line" d="M14 19H40V36H14Z" />
        <path className="icon-line" d="M14 19L27 29L40 19" />
      </g>
    );
  }

  if (type === "chat") {
    return (
      <g transform={`translate(${x} ${y})`}>
        <rect className="icon-box" width="54" height="54" rx="13" />
        <path className="icon-line" d="M15 18H39V32H25L18 39V32H15Z" />
        <path className="icon-muted" d="M21 24H34M21 29H30" />
      </g>
    );
  }

  if (type === "voice") {
    return (
      <g transform={`translate(${x} ${y})`}>
        <rect className="icon-box" width="54" height="54" rx="13" />
        {[0, 1, 2, 3, 4].map((bar) => (
          <rect
            key={bar}
            className="voice-bar"
            x={14 + bar * 6}
            y="16"
            width="3.5"
            height="22"
            rx="1.8"
            style={{ animationDelay: `${bar * 0.12}s` }}
          />
        ))}
      </g>
    );
  }

  if (type === "calendar") {
    return (
      <g transform={`translate(${x} ${y})`}>
        <rect className="icon-box" width="54" height="54" rx="13" />
        <path className="icon-line" d="M16 20H38V39H16Z" />
        <path className="icon-line" d="M16 26H38M22 16V22M32 16V22" />
        <path className="icon-muted" d="M21 31H24M29 31H32M21 36H24M29 36H32" />
      </g>
    );
  }

  if (type === "profile") {
    return (
      <g transform={`translate(${x} ${y})`}>
        <rect className="icon-box" width="54" height="54" rx="13" />
        <circle className="icon-line-fill" cx="27" cy="22" r="8" />
        <path className="icon-line-fill" d="M14 42C16.4 33.5 37.6 33.5 40 42Z" />
      </g>
    );
  }

  if (type === "checklist") {
    return (
      <g transform={`translate(${x} ${y})`}>
        <rect className="icon-box" width="54" height="54" rx="13" />
        <path className="icon-line" d="M16 21L20 25L27 17M16 34L20 38L27 30" />
        <path className="icon-muted" d="M32 22H40M32 35H40" />
      </g>
    );
  }

  if (type === "chart") {
    return (
      <g transform={`translate(${x} ${y})`}>
        <rect className="icon-box" width="54" height="54" rx="13" />
        <path className="icon-line" d="M15 39H40" />
        <rect className="chart-bar" x="18" y="31" width="5" height="8" rx="2" />
        <rect className="chart-bar" x="26" y="25" width="5" height="14" rx="2" />
        <rect className="chart-bar" x="34" y="17" width="5" height="22" rx="2" />
      </g>
    );
  }

  return (
    <g transform={`translate(${x} ${y})`}>
      <rect className="icon-box" width="54" height="54" rx="13" />
      <path className="icon-line" d="M18 14H33L40 21V40H18Z" />
      <path className="icon-line" d="M33 14V21H40" />
      <path className="icon-muted" d="M23 28H35M23 34H34" />
    </g>
  );
}

function InputCard({ item }) {
  return (
    <g
      data-ai-input-card
      className="work-card input-card"
      transform={`translate(${item.x} ${item.y})`}
      style={{ animationDelay: item.delay }}
    >
      <rect className="card-bg" width="272" height="90" rx="18" />
      <Icon type={item.icon} x={18} y={12} />
      <text data-ai-card-label className="card-label icon-label" x="45" y="83" textAnchor="middle">
        {item.label}
      </text>
      <circle className="status-dot" cx="104" cy="25" r="5" />
      <path className="card-line wide" d="M118 24H204" />
      <path className="card-line" d="M118 42H238" />
      <path className="card-line short" d="M118 60H188" />
    </g>
  );
}

function OutputCard({ item }) {
  return (
    <g
      data-ai-output-card
      className="work-card output-card"
      transform={`translate(${item.x} ${item.y})`}
      style={{ animationDelay: item.delay }}
    >
      <rect className="card-bg" width="270" height="98" rx="18" />
      <Icon type={item.icon} x={20} y={16} />
      <text data-ai-card-label className="card-label icon-label" x="47" y="86" textAnchor="middle">
        {item.label}
      </text>
      <path className="card-line wide" d="M100 27H218" />
      <path className="card-line" d="M100 47H244" />
      <path className="card-line short" d="M100 67H190" />
      <rect className="output-fill" x="100" y="77" width="64" height="6" rx="3" />
    </g>
  );
}

export default function AiOperationsVisual({ className, title }) {
  const { i18n } = useTranslation();
  const isNl = i18n.language?.startsWith("nl");
  const labels = {
    aria: isNl ? "Visual van AI in de operatie" : "AI operations visual",
    core: isNl ? "AI verdeelt werk" : "AI routes work",
    process: isNl ? "Proces" : "Process",
    processSub: isNl ? "regels + systemen" : "rules + systems",
    review: isNl ? "Menselijke check" : "Human review",
    reviewSub: isNl ? "akkoord voor live" : "approval before live",
  };
  const inputs = INPUTS.map((item) => ({ ...item, label: isNl ? item.label.nl : item.label.en }));
  const outputs = OUTPUTS.map((item) => ({ ...item, label: isNl ? item.label.nl : item.label.en }));

  return (
    <Frame
      className={className}
      data-ai-operations-visual
      role="img"
      aria-label={title || labels.aria}
    >
      <svg viewBox="0 0 1000 940" focusable="false">
        <defs>
          <radialGradient id="ai-core-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(205, 235, 243, 0.95)" />
            <stop offset="34%" stopColor="rgba(96, 188, 212, 0.44)" />
            <stop offset="100%" stopColor="rgba(96, 188, 212, 0)" />
          </radialGradient>
          <linearGradient id="ai-line" x1="0%" x2="100%">
            <stop offset="0%" stopColor="rgba(255,255,255,0)" />
            <stop offset="45%" stopColor="rgba(206,236,244,0.92)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
        </defs>

        <rect className="outer-panel" x="18" y="18" width="964" height="904" rx="38" />
        <g className="decor-rails">
          <path data-ai-decor-rail className="circuit" d="M500 890V914M466 904H534" />
        </g>

        <g className="flow-layer">
          {INPUT_PATHS.map((path, index) => (
            <React.Fragment key={path}>
              <path className="track-line" d={path} />
              <path
                data-ai-flow-path
                className="flow-line"
                d={path}
                style={{ animationDelay: `${index * 0.22}s` }}
              />
            </React.Fragment>
          ))}
          {OUTPUT_PATHS.map((path, index) => (
            <React.Fragment key={path}>
              <path className="track-line" d={path} />
              <path
                data-ai-flow-path
                className="flow-line output-flow"
                d={path}
                style={{ animationDelay: `${1.1 + index * 0.24}s` }}
              />
            </React.Fragment>
          ))}
        </g>

        {inputs.map((item) => (
          <InputCard key={item.id} item={item} />
        ))}

        {outputs.map((item) => (
          <OutputCard key={item.id} item={item} />
        ))}

        <g className="core" transform="translate(500 370)">
          <circle className="core-halo" r="126" />
          <circle className="core-ring ring-a" r="100" />
          <circle className="core-ring ring-b" r="76" />
          <circle className="core-glow" r="72" />
          <g className="network">
            <path className="network-line" d="M-42 -16L-8 -42L35 -28L48 12L16 48L-38 36L-52 4Z" />
            <path className="network-line" d="M-42 -16L16 48M-8 -42L-38 36M35 -28L-52 4M48 12L-8 -42" />
            {[
              [-42, -16], [-8, -42], [35, -28], [48, 12],
              [16, 48], [-38, 36], [-52, 4], [0, 0],
            ].map(([cx, cy]) => (
              <circle key={`${cx}-${cy}`} className="network-dot" cx={cx} cy={cy} r="5" />
            ))}
          </g>
          <text className="core-label" x="0" y="150" textAnchor="middle">{labels.core}</text>
        </g>

        <g data-ai-automation-card className="automation-card" transform="translate(320 676)">
          <rect className="card-bg" width="360" height="90" rx="18" />
          <g data-ai-premium-workflow-icon className="workflow-glyph" transform="translate(24 15)">
            <rect className="logic-block accent" x="42" y="0" width="28" height="20" rx="6" />
            <rect className="logic-block" x="7" y="42" width="28" height="16" rx="5" />
            <rect className="logic-block" x="77" y="42" width="28" height="16" rx="5" />
            <path className="workflow-route base" d="M56 20V35H21V42M56 35H91V42" />
            <path className="workflow-route active" d="M56 20V35H21V42M56 35H91V42" />
            <circle className="logic-node" cx="56" cy="35" r="3.8" />
          </g>
          <text className="workflow-title" x="154" y="35">{labels.process}</text>
          <text className="workflow-subtitle" x="154" y="60">{labels.processSub}</text>
          <path className="card-line short workflow-line" d="M154 72H262" />
        </g>

        <path className="flow-line review-flow" d="M500 766V792" />
        <g data-ai-review-control className="review-card" transform="translate(260 792)">
          <rect className="card-bg" width="480" height="92" rx="22" />
          <g className="review-shield" transform="translate(40 22)">
            <path className="shield" d="M24 0L44 7V22C44 36 33 43 24 46C15 43 4 36 4 22V7Z" />
            <path className="shield-check" d="M16 23L22 29L34 15" />
          </g>
          <text data-ai-review-title className="review-title" x="112" y="35">{labels.review}</text>
          <text className="review-subtitle" x="112" y="60">{labels.reviewSub}</text>
          <g data-ai-review-people className="review-people" transform="translate(374 47)">
            {[0, 1, 2].map((index) => (
              <g data-ai-review-person key={index} transform={`translate(${index * 31} 0)`}>
                <circle className={index === 2 ? "human active" : "human"} cy="-8" r="8" />
                <path className={index === 2 ? "human-line active" : "human-line"} d="M-10 15C-7 7 7 7 10 15" />
              </g>
            ))}
          </g>
          <path className="review-divider" d="M330 22V70" />
        </g>
      </svg>
    </Frame>
  );
}

const Frame = styled.div`
  width: 100%;
  aspect-ratio: 16 / 15;
  min-height: 0;
  overflow: hidden;
  border-radius: 24px;
  color: rgba(255, 255, 255, 0.88);
  background:
    radial-gradient(circle at 50% 42%, rgba(121, 201, 222, 0.12), transparent 30%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.018)),
    rgba(4, 6, 8, 0.52);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 22px 60px rgba(0, 0, 0, 0.18);

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  .outer-panel {
    fill: rgba(255, 255, 255, 0.018);
    stroke: rgba(255, 255, 255, 0.44);
    stroke-width: 1.4;
  }

  .circuit {
    fill: none;
    stroke: rgba(255, 255, 255, 0.08);
    stroke-width: 1.2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .card-bg {
    fill: rgba(255, 255, 255, 0.045);
    stroke: rgba(255, 255, 255, 0.2);
    stroke-width: 1.2;
  }

  .work-card {
    animation: ${cardWake} 5.8s ease-in-out infinite;
  }

  .icon-box {
    fill: rgba(255, 255, 255, 0.055);
    stroke: rgba(255, 255, 255, 0.13);
    stroke-width: 1.1;
  }

  .icon-line,
  .icon-muted,
  .automation-icon,
  .shield-check {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .icon-line {
    stroke: rgba(225, 235, 238, 0.86);
    stroke-width: 2.6;
  }

  .icon-muted {
    stroke: rgba(177, 203, 210, 0.54);
    stroke-width: 2.4;
  }

  .icon-line-fill {
    fill: rgba(225, 235, 238, 0.86);
  }

  .voice-bar,
  .chart-bar {
    fill: rgba(151, 214, 232, 0.84);
    transform-box: fill-box;
    transform-origin: center;
  }

  .voice-bar {
    animation: ${voiceMove} 1.15s ease-in-out infinite;
  }

  .status-dot,
  .network-dot {
    fill: rgba(123, 210, 236, 0.95);
    filter: drop-shadow(0 0 7px rgba(123, 210, 236, 0.34));
  }

  .card-line {
    fill: none;
    stroke: rgba(166, 190, 198, 0.36);
    stroke-width: 5;
    stroke-linecap: round;
  }

  .card-line.wide {
    stroke-width: 6;
  }

  .card-line.short {
    stroke: rgba(166, 190, 198, 0.28);
  }

  .card-label,
  .core-label {
    fill: rgba(255, 255, 255, 0.62);
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  .icon-label {
    fill: rgba(255, 255, 255, 0.54);
    font-size: 10.4px;
    letter-spacing: 0.105em;
  }

  .track-line,
  .flow-line {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .track-line {
    stroke: rgba(255, 255, 255, 0.16);
    stroke-width: 2;
  }

  .flow-line {
    stroke: url(#ai-line);
    stroke-width: 3;
    stroke-dasharray: 38 142;
    animation: ${flow} 3.6s linear infinite;
    filter: drop-shadow(0 0 6px rgba(120, 217, 243, 0.28));
  }

  .output-flow {
    stroke-width: 3.4;
  }

  .core {
    transform-box: fill-box;
    transform-origin: center;
  }

  .core-halo {
    fill: rgba(255, 255, 255, 0.02);
    stroke: rgba(123, 210, 236, 0.22);
    stroke-width: 1;
  }

  .core-ring {
    fill: none;
    stroke: rgba(123, 210, 236, 0.34);
    stroke-width: 1.2;
    stroke-dasharray: 12 18;
    transform-box: fill-box;
    transform-origin: center;
    animation: ${coreSpin} 18s linear infinite;
  }

  .ring-b {
    animation-direction: reverse;
    animation-duration: 14s;
  }

  .core-glow {
    fill: url(#ai-core-glow);
    animation: ${softPulse} 2.8s ease-in-out infinite;
    transform-box: fill-box;
    transform-origin: center;
  }

  .network-line {
    fill: none;
    stroke: rgba(205, 235, 243, 0.56);
    stroke-width: 1.4;
  }

  .logic-block,
  .automation-box {
    fill: rgba(255, 255, 255, 0.045);
    stroke: rgba(255, 255, 255, 0.22);
    stroke-width: 1.2;
  }

  .logic-block.accent,
  .automation-box.accent {
    fill: rgba(123, 210, 236, 0.24);
    stroke: rgba(123, 210, 236, 0.76);
  }

  .workflow-route {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .workflow-route.base {
    stroke: rgba(205, 235, 243, 0.34);
    stroke-width: 3.2;
  }

  .workflow-route.active {
    stroke: rgba(123, 210, 236, 0.92);
    stroke-width: 3.4;
    stroke-dasharray: 22 74;
    animation: ${routeTrace} 2.8s linear infinite;
    filter: drop-shadow(0 0 5px rgba(123, 210, 236, 0.24));
  }

  .logic-node {
    fill: rgba(205, 235, 243, 0.86);
    transform-box: fill-box;
    transform-origin: center;
    animation: ${nodeSignal} 2.8s ease-in-out infinite;
  }

  .workflow-title,
  .review-title {
    fill: rgba(255, 255, 255, 0.78);
    font-size: 15px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .workflow-subtitle,
  .review-subtitle {
    fill: rgba(205, 220, 225, 0.5);
    font-size: 11.2px;
    font-weight: 700;
    letter-spacing: 0.08em;
  }

  .workflow-line {
    stroke: rgba(123, 210, 236, 0.42);
    stroke-width: 3.4;
  }

  .output-fill {
    fill: rgba(123, 210, 236, 0.8);
    transform-box: fill-box;
    transform-origin: left center;
    animation: ${outputFill} 4.2s ease-in-out infinite;
  }

  .review-flow {
    stroke-width: 3.4;
  }

  .review-divider {
    fill: none;
    stroke: rgba(255, 255, 255, 0.1);
    stroke-width: 1;
  }

  .shield {
    fill: rgba(225, 235, 238, 0.9);
  }

  .shield-check {
    stroke: rgba(8, 12, 14, 0.84);
    stroke-width: 3;
  }

  .human {
    fill: rgba(255, 255, 255, 0.1);
    stroke: rgba(255, 255, 255, 0.24);
    stroke-width: 1.2;
  }

  .human-line {
    fill: none;
    stroke: rgba(255, 255, 255, 0.22);
    stroke-width: 1.2;
  }

  .human.active {
    fill: rgba(123, 210, 236, 0.32);
    stroke: rgba(123, 210, 236, 0.88);
  }

  .human-line.active {
    stroke: rgba(123, 210, 236, 0.88);
  }

  @media (prefers-reduced-motion: reduce) {
    .work-card,
    .flow-line,
    .core-ring,
    .core-glow,
    .voice-bar,
    .output-fill,
    .workflow-route.active,
    .logic-node {
      animation: none !important;
    }
  }
`;
